import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map features to sidebar navigation items
const FEATURE_TO_NAV_MAP = {
  // Core features (Starter)
  'invoicing': ['invoices', 'accounts-receivable', 'accounts-payable'],
  'clients': ['clients'],
  'services_goods': ['services'],
  'basic_reports': ['analytics'],
  'email_invoice': [], // Feature flag, no nav item
  'tax_settings': ['tax-documents'],
  
  // Team features (Professional)
  'employees': ['employees'],
  'portal_management': ['portal-settings'],
  
  // Operations features (Professional)
  'equipment': ['equipment'],
  'fleet': ['fleet'],
  'documents': ['documents', 'document-templates'],
  
  // HR features (Professional)
  'training': ['training'],
  'safety': ['safety'],
  'policies': ['policies'],
  'certifications': ['certifications'],
  
  // Finance features (Enterprise)
  'payroll': ['payroll'],
  'payroll_automation': [], // Feature flag for payroll
  
  // Advanced features (Enterprise)
  'unions': ['union', 'unions'],
  'advanced_analytics': [], // Enhanced analytics
  'audit_logs': ['audit-logs'],
};

// Routes that are always available (don't require feature check)
const ALWAYS_AVAILABLE_ROUTES = [
  'dashboard',
  'business-profile',
  'calendar',
  'quotes',
  'categories',
  'terms-conditions',
];

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMaster, setIsMaster] = useState(false);

  const fetchSubscriptionStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setSubscription(null);
      return;
    }

    // Set loading to true while we fetch
    setLoading(true);

    try {
      // First check if user is admin
      const adminCheck = await fetch(`${API_URL}/api/admin/access-check`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (adminCheck.ok) {
        const adminData = await adminCheck.json();
        setIsAdmin(adminData.has_admin_access);
        setIsMaster(adminData.is_master);
        
        // Admins have access to everything
        if (adminData.has_admin_access) {
          setFeatures(Object.keys(FEATURE_TO_NAV_MAP));
          setSubscription({ plan: 'admin', status: 'active', has_subscription: true });
          setLoading(false);
          return;
        }
      }

      // For non-admin users, check subscription status
      const response = await fetch(`${API_URL}/api/subscriptions/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        setFeatures(data.features || []);
      } else {
        // User might be legacy (no tenant) - give them full access for now
        // This handles the case where existing users don't have subscriptions
        setFeatures(Object.keys(FEATURE_TO_NAV_MAP));
        setSubscription({ plan: 'legacy', status: 'active', has_subscription: true });
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // On error, assume full access to not break existing functionality
      setFeatures(Object.keys(FEATURE_TO_NAV_MAP));
      setSubscription({ plan: 'legacy', status: 'active', has_subscription: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus, user]);

  // Check if a specific feature is enabled
  const hasFeature = useCallback((feature) => {
    if (isAdmin) return true;
    return features.includes(feature);
  }, [features, isAdmin]);

  // Check if a route/nav item should be visible
  const canAccessRoute = useCallback((route) => {
    // Always available routes
    if (ALWAYS_AVAILABLE_ROUTES.includes(route)) {
      return true;
    }

    // Admins can access everything
    if (isAdmin) {
      return true;
    }

    // Check if any feature grants access to this route
    for (const [feature, routes] of Object.entries(FEATURE_TO_NAV_MAP)) {
      if (routes.includes(route) && features.includes(feature)) {
        return true;
      }
    }

    return false;
  }, [features, isAdmin]);

  // Get list of routes user can access
  const getAccessibleRoutes = useCallback(() => {
    if (isAdmin) {
      return [...ALWAYS_AVAILABLE_ROUTES, ...Object.values(FEATURE_TO_NAV_MAP).flat()];
    }

    const accessible = [...ALWAYS_AVAILABLE_ROUTES];
    for (const [feature, routes] of Object.entries(FEATURE_TO_NAV_MAP)) {
      if (features.includes(feature)) {
        accessible.push(...routes);
      }
    }
    return [...new Set(accessible)];
  }, [features, isAdmin]);

  // Get the feature required for a route
  const getRequiredFeature = useCallback((route) => {
    for (const [feature, routes] of Object.entries(FEATURE_TO_NAV_MAP)) {
      if (routes.includes(route)) {
        return feature;
      }
    }
    return null;
  }, []);

  const value = {
    loading,
    subscription,
    features,
    isAdmin,
    isMaster,
    hasFeature,
    canAccessRoute,
    getAccessibleRoutes,
    getRequiredFeature,
    refreshSubscription: fetchSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
